import { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, ShieldWarning } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PasswordInput from '@/components/PasswordInput';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { API } from '@/App';

/**
 * Full-screen, non-dismissable overlay shown when user.mustChangePassword === true.
 * The user cannot navigate or interact with the app until they complete the change.
 * After success, calls onComplete() which should refresh user data.
 */
export default function ForcePasswordChange({ user, token, onComplete, onLogout }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 12) {
      toast.error('New password must be at least 12 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New password and confirmation do not match');
      return;
    }
    if (newPassword === currentPassword) {
      toast.error('New password must differ from the one-time password');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword, confirmPassword }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        toast.success('Password updated. You can now use the dashboard.');
        await onComplete();
      } else {
        toast.error(data.detail || `Failed to change password (HTTP ${response.status})`);
      }
    } catch (err) {
      toast.error(`Network error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="force-password-change-overlay"
      className="fixed inset-0 z-[9999] bg-[#0e0b04]/95 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="w-full max-w-md bg-[#1a1508] border border-[#cba135]/30 rounded-xl shadow-[0_20px_60px_rgba(0,0,0,0.8)] p-6 sm:p-8"
      >
        <div className="flex items-start gap-3 mb-5">
          <div className="p-2.5 bg-[#cba135]/10 rounded-lg">
            <ShieldWarning size={28} weight="duotone" className="text-[#cba135]" />
          </div>
          <div>
            <h2 className="heading-font text-xl sm:text-2xl font-medium text-[#cba135]">
              Set a permanent password
            </h2>
            <p className="text-xs text-[#cba135]/60 mt-1">
              Logged in as <span className="text-[#cba135] font-semibold">{user?.email}</span>
            </p>
          </div>
        </div>

        <div className="mb-5 p-3 bg-[#cba135]/8 border border-[#cba135]/20 rounded text-xs leading-relaxed text-[#cba135]/85">
          For security reasons you must replace the one-time password before continuing.
          Minimum 12 characters. Choose something you can remember and store in a password manager.
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="fpc-current" className="text-xs uppercase tracking-[0.15em] font-bold text-[#a8872b]">
              One-time password
            </Label>
            <PasswordInput
              id="fpc-current"
              data-testid="fpc-current-password"
              autoComplete="current-password"
              autoFocus
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              className="mt-1.5 bg-[#0e0b04] border-[#cba135]/30 text-[#cba135]"
            />
          </div>
          <div>
            <Label htmlFor="fpc-new" className="text-xs uppercase tracking-[0.15em] font-bold text-[#a8872b]">
              New password (min 12 chars)
            </Label>
            <PasswordInput
              id="fpc-new"
              data-testid="fpc-new-password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={12}
              className="mt-1.5 bg-[#0e0b04] border-[#cba135]/30 text-[#cba135]"
            />
          </div>
          <div>
            <Label htmlFor="fpc-confirm" className="text-xs uppercase tracking-[0.15em] font-bold text-[#a8872b]">
              Confirm new password
            </Label>
            <PasswordInput
              id="fpc-confirm"
              data-testid="fpc-confirm-password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={12}
              className="mt-1.5 bg-[#0e0b04] border-[#cba135]/30 text-[#cba135]"
            />
          </div>

          <div className="flex flex-col sm:flex-row-reverse gap-2 pt-2">
            <Button
              type="submit"
              data-testid="fpc-submit"
              disabled={loading}
              className="bg-[#cba135] text-[#0e0b04] hover:bg-[#d4b347] font-semibold flex-1"
            >
              <Lock size={16} weight="bold" className="mr-2" />
              {loading ? 'Saving…' : 'Set permanent password'}
            </Button>
            <Button
              type="button"
              data-testid="fpc-cancel"
              variant="outline"
              onClick={onLogout}
              disabled={loading}
              className="border-[#cba135]/20 text-[#cba135]/70 hover:bg-[#cba135]/5 hover:text-[#cba135] sm:flex-none"
            >
              Sign out
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
